import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import {
  BarChart3,
  TrendingUp,
  Clock,
  Users,
  BookOpen,
  CheckCircle,
  Play,
  Eye,
  Download
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Button } from '../components/ui/button';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend
} from 'recharts';

const COLORS = ['#2563EB', '#16A34A', '#EAB308', '#DC2626', '#8B5CF6'];

const AnalyticsPage = () => {
  const { api } = useAuth();
  const [trainings, setTrainings] = useState([]);
  const [selectedTraining, setSelectedTraining] = useState(null);
  const [trainingAnalytics, setTrainingAnalytics] = useState(null);
  const [userAnalytics, setUserAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedTraining) {
      fetchTrainingAnalytics(selectedTraining);
    }
  }, [selectedTraining]);

  const fetchData = async () => {
    try {
      const [trainingsRes, userRes] = await Promise.all([
        api.get('/trainings'),
        api.get('/analytics/user')
      ]);
      setTrainings(trainingsRes.data);
      setUserAnalytics(userRes.data);
      
      if (trainingsRes.data.length > 0) {
        setSelectedTraining(trainingsRes.data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrainingAnalytics = async (trainingId) => {
    try {
      const response = await api.get(`/analytics/training/${trainingId}`);
      setTrainingAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch training analytics:', error);
    }
  };

  const formatTime = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)}min`;
    return `${Math.round(seconds / 3600)}h ${Math.round((seconds % 3600) / 60)}min`;
  };

  // Prepare chart data
  const trainingChartData = trainings.map(t => ({
    name: t.title.length > 15 ? t.title.substring(0, 15) + '...' : t.title,
    modules: t.module_count,
    lessons: t.lesson_count
  }));

  const statusChartData = [
    { name: 'Brouillon', value: trainings.filter(t => t.status === 'draft').length },
    { name: 'En cours', value: trainings.filter(t => t.status === 'in_progress').length },
    { name: 'Prêt', value: trainings.filter(t => t.status === 'ready').length },
    { name: 'Publié', value: trainings.filter(t => t.status === 'published').length }
  ].filter(d => d.value > 0);

  const progressChartData = [
    { name: 'Commencées', value: userAnalytics?.lessons_started || 0 },
    { name: 'Terminées', value: userAnalytics?.lessons_completed || 0 }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-8 h-8 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  const overviewStats = [
    {
      title: 'Formations créées',
      value: trainings.length,
      icon: BookOpen,
      color: 'text-blue-600',
      bg: 'bg-blue-50'
    },
    {
      title: 'Leçons complétées',
      value: userAnalytics?.lessons_completed || 0,
      icon: CheckCircle,
      color: 'text-emerald-600',
      bg: 'bg-emerald-50'
    },
    {
      title: 'Temps total',
      value: formatTime(userAnalytics?.total_watch_time || 0),
      icon: Clock,
      color: 'text-amber-600',
      bg: 'bg-amber-50'
    },
    {
      title: 'Taux de complétion',
      value: `${Math.round(userAnalytics?.completion_rate || 0)}%`,
      icon: TrendingUp,
      color: 'text-purple-600',
      bg: 'bg-purple-50'
    }
  ];

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl lg:text-3xl font-semibold text-slate-900 font-['Outfit']">
            Analytics
          </h1>
          <p className="text-slate-500 mt-1">
            Suivez l'engagement de vos formations
          </p>
        </div>
        <Button variant="outline" className="gap-2">
          <Download className="w-4 h-4" />
          Exporter
        </Button>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {overviewStats.map((stat, i) => (
          <Card key={i} className="border border-slate-200 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-slate-500 mb-1">{stat.title}</p>
                  <p className="text-2xl font-semibold text-slate-900">{stat.value}</p>
                </div>
                <div className={`w-10 h-10 rounded-lg ${stat.bg} flex items-center justify-center`}>
                  <stat.icon className={`w-5 h-5 ${stat.color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        {/* Training Overview Chart */}
        <Card className="border border-slate-200">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="text-lg">Contenu par formation</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {trainingChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={trainingChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#64748B' }} />
                  <YAxis tick={{ fontSize: 12, fill: '#64748B' }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#FFF', 
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px'
                    }} 
                  />
                  <Legend />
                  <Bar dataKey="modules" name="Modules" fill="#2563EB" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="lessons" name="Leçons" fill="#16A34A" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center text-slate-400">
                Aucune donnée disponible
              </div>
            )}
          </CardContent>
        </Card>

        {/* Status Distribution */}
        <Card className="border border-slate-200">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="text-lg">Répartition par statut</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {statusChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={statusChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  >
                    {statusChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center text-slate-400">
                Aucune donnée disponible
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Training Details */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="border border-slate-200">
          <CardHeader className="border-b border-slate-100">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Détails par formation</CardTitle>
              <Select value={selectedTraining || ''} onValueChange={setSelectedTraining}>
                <SelectTrigger className="w-48" data-testid="training-select">
                  <SelectValue placeholder="Sélectionner" />
                </SelectTrigger>
                <SelectContent>
                  {trainings.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            {trainingAnalytics ? (
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500 mb-1">Leçons</p>
                    <p className="text-2xl font-semibold text-slate-900">
                      {trainingAnalytics.total_lessons}
                    </p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500 mb-1">Apprenants</p>
                    <p className="text-2xl font-semibold text-slate-900">
                      {trainingAnalytics.unique_learners}
                    </p>
                  </div>
                </div>
                
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-slate-600">Taux de complétion</span>
                    <span className="font-semibold text-slate-900">
                      {Math.round(trainingAnalytics.completion_rate)}%
                    </span>
                  </div>
                  <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all"
                      style={{ width: `${trainingAnalytics.completion_rate}%` }}
                    />
                  </div>
                </div>
                
                <div className="p-4 bg-blue-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="text-sm text-blue-600">Temps de visionnage total</p>
                      <p className="font-semibold text-blue-900">
                        {formatTime(trainingAnalytics.total_watch_time)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : trainings.length === 0 ? (
              <div className="text-center py-8">
                <BarChart3 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Créez votre première formation pour voir les analytics</p>
              </div>
            ) : (
              <div className="flex items-center justify-center py-8">
                <div className="w-6 h-6 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Progress Overview */}
        <Card className="border border-slate-200">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="text-lg">Progression globale</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {userAnalytics && (userAnalytics.lessons_started > 0 || userAnalytics.lessons_completed > 0) ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={progressChartData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis type="number" tick={{ fontSize: 12, fill: '#64748B' }} />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 12, fill: '#64748B' }} width={100} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#FFF', 
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px'
                    }} 
                  />
                  <Bar dataKey="value" fill="#2563EB" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-8">
                <Eye className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Commencez une formation pour voir votre progression</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Activity List */}
      <Card className="border border-slate-200 mt-6">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="text-lg">Activité récente</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {trainings.length === 0 ? (
            <div className="p-8 text-center">
              <Eye className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Aucune activité pour le moment</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {trainings.slice(0, 5).map((training) => (
                <div 
                  key={training.id}
                  className="flex items-center gap-4 p-4 hover:bg-slate-50 transition-colors"
                >
                  <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-slate-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 truncate">
                      {training.title}
                    </p>
                    <p className="text-sm text-slate-500">
                      {training.module_count} modules • {training.lesson_count} leçons
                    </p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    training.status === 'published' 
                      ? 'bg-emerald-100 text-emerald-700'
                      : training.status === 'ready'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-slate-100 text-slate-600'
                  }`}>
                    {training.status === 'published' ? 'Publié' 
                      : training.status === 'ready' ? 'Prêt'
                      : training.status === 'in_progress' ? 'En cours'
                      : 'Brouillon'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AnalyticsPage;
